import { Theme } from "@mui/material";
import { isEmpty, truncate } from "lodash";

import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { AggregatedData } from "@/pages/channel-roi/types";
import { Palette } from "@/Utils/Colors/palette";
import { calculateBubbleSize } from "@/Components/v2/molecule/BubbleChart/calculateBubbleSize";

import { formatTooltip } from "./formatTooltip";

const THRESHOLD_LINES = {
  ROAS: 200,
  MEDIA_CONTRIBUTION: 10,
};

export const SPEND_THRESHOLD = 1000;

type CornerLabelPosition = {
  left?: string;
  right?: string;
  top?: string;
  bottom?: string;
};

const CORNER_LABEL_COLOR = "rgba(246, 241, 255, 0.50)";

const createBackgroundRect = (isRightAligned: boolean) => ({
  type: "rect",
  right: isRightAligned ? "-20%" : undefined,
  shape: {
    width: 110,
    height: 45,
    r: 4,
  },
  style: {
    fill: CORNER_LABEL_COLOR,
  },
});

const createLabelText = (text: string, isRightAligned: boolean, theme: Theme) => ({
  type: "text",
  left: isRightAligned ? undefined : 12,
  right: isRightAligned ? 12 : undefined,
  top: 8,
  style: {
    text,
    fontSize: 14,
    fill: theme.palette.text.primary,
    ...(isRightAligned && {
      align: "right",
      width: 116,
    }),
  },
});

const createCornerLabel = ({
  position,
  text,
  isRightAligned,
  theme,
}: {
  position: CornerLabelPosition;
  text: string;
  isRightAligned: boolean;
  theme: Theme;
}) => ({
  type: "group",
  ...position,
  children: [createBackgroundRect(isRightAligned), createLabelText(text, isRightAligned, theme)],
});

const cornerLabelOptions = [
  {
    position: { left: "6%", top: "9%" },
    text: "Low efficiency\nHigh volume",
    isRightAligned: false,
  },
  {
    position: { right: "2%", top: "9%" },
    text: "High efficiency\nHigh volume",
    isRightAligned: true,
  },
  {
    position: { left: "6%", bottom: "14%" },
    text: "Low efficiency\nLow volume",
    isRightAligned: false,
  },
  {
    position: { right: "2%", bottom: "14%" },
    text: "High efficiency\nLow volume",
    isRightAligned: true,
  },
];

const comparisonTextStyle = (theme: Theme) => ({
  rich: {
    nodeName: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.text.primary,
      align: "center",
      padding: [0, 0, 4, 0],
    },
    channelName: {
      fontSize: 14,
      fontWeight: 600,
      color: theme.palette.text.primary,
      align: "center",
    },
  },
});

const getComparisonLabel = (nodeName: string, channelName: string) => {
  return [`{nodeName|${nodeName}}`, `{channelName|${channelName}}`].join("\n");
};

type BubbleChartData = AggregatedData & {
  nodeName?: string;
  isComparisonNode?: boolean;
  insufficientSpend?: boolean;
};

// TODO: Clean this up, has got bloated
export const bubbleChartOptions = (
  nodes: BubbleChartData[],
  theme: Theme,
  palette: Palette,
  formatCurrency: FormatCurrencyFunction,
  targetNodeName: string,
  comparisonNodeData: BubbleChartData[]
) => {
  // Calculate spend metrics once at the top level
  const isComparing = !isEmpty(comparisonNodeData);

  const allNodes = nodes;
  if (isComparing && comparisonNodeData) allNodes.push(...comparisonNodeData);

  const allSpends = allNodes.map((node) => node.spend.total);
  const minSpend = Math.min(...allSpends);
  const maxSpend = Math.max(...allSpends);

  // Group nodes by channel - required for emphasis to work.
  const channelGroups = allNodes.reduce(
    (acc, node) => {
      if (!acc[node.channel]) {
        acc[node.channel] = [];
      }
      if (node.spend.total < SPEND_THRESHOLD) {
        acc[node.channel]!.push({
          ...node,
          insufficientSpend: true,
        });
      } else {
        // Put items with insufficient spend at the end of the array
        acc[node.channel]!.unshift(node);
      }
      return acc;
    },
    {} as Record<string, typeof allNodes>
  );
  // Create series data for each channel
  const seriesData = Object.entries(channelGroups).map(([channel, nodes]) => ({
    name: channel,
    type: "scatter",
    data: nodes
      .filter((item) => item.spend.total > SPEND_THRESHOLD)
      .map((item) => ({
        name: item.channel,
        value: [item.roas.total, item.contribution.total, item.spend.total],
        itemStyle: {
          color: palette.chooseColorMemo(item.channel).color,
          opacity: item.isComparisonNode ? 0.6 : 1,
          borderColor: item.isComparisonNode
            ? palette.chooseColorMemo(item.channel).color
            : undefined,
          borderWidth: 2,
        },
      })),
    symbolSize: (data: number[]) => {
      const spend = data[2] ?? 0;
      return calculateBubbleSize(spend, minSpend, maxSpend);
    },
    emphasis: {
      focus: "series",
    },
    blur: {
      label: {
        show: false,
      },
    },
    label: {
      show: true,
      position: "inside",
      formatter: (param: any) => {
        const node = nodes[param.dataIndex] as AggregatedData & { nodeName?: string };
        return isComparing
          ? getComparisonLabel(node?.nodeName || targetNodeName, param.name)
          : truncate(param.name, { length: 16 });
      },
      color: theme.palette.text.primary,
      fontWeight: 600,
      opacity: 1,
      ...comparisonTextStyle(theme),
    },
  }));

  return {
    tooltip: {
      trigger: "item",
      formatter: (params: any) =>
        formatTooltip(params, formatCurrency, theme, targetNodeName, channelGroups[params.name]),
      backgroundColor: theme.palette.background.paper,
      borderWidth: 0,
      padding: 0,
      className: "echarts-tooltip-dark",
    },
    grid: {
      left: "1%",
      right: "2%",
      bottom: "6%",
      containLabel: true,
    },
    xAxis: {
      type: "value",
      name: "ROAS % (Efficiency)",
      nameLocation: "middle",
      nameGap: 45,
      splitLine: {
        show: false,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: theme.palette.text.primary,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: theme.palette.text.primary,
        fontSize: 14,
        margin: 24,
      },
      nameTextStyle: {
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: "14px",
        padding: [10, 0, 0, 0],
      },
    },
    yAxis: {
      type: "value",
      name: "Media Contribution % (Volume)",
      nameLocation: "end",
      nameGap: 40,
      nameTextStyle: {
        align: "center",
        verticalAlign: "top",
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: "14px",
        padding: [0, 20, 0, 140],
      },
      splitLine: {
        show: false,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: theme.palette.text.primary,
        },
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: theme.palette.text.primary,
        fontSize: 14,
        margin: 24,
      },
    },
    series: [
      {
        type: "line",
        markLine: {
          silent: true,
          symbol: "none",
          label: { show: false },
          data: [{ xAxis: THRESHOLD_LINES.ROAS }, { yAxis: THRESHOLD_LINES.MEDIA_CONTRIBUTION }],
          lineStyle: {
            type: "dashed",
            color: theme.palette.bubbleChart.dashedLine,
          },
        },
      },
      ...seriesData,
    ],
    graphic: cornerLabelOptions.map((option) => createCornerLabel({ ...option, theme })),
  };
};
